<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-buzz-post-body">
    <buzz-post-text
      v-if="post.text"
      tag="p"
      :text="post.text"
      :text-class="postClasses"
    />
    <oxd-text
      v-show="!readMore"
      tag="p"
      class="orangehrm-buzz-post-body-readmore"
      @click="onClickReadMore"
    >
      {{ $t('buzz.read_more') }}
    </oxd-text>
    <buzz-link-preview
      v-if="readMore && post.text && post.type !== 'video'"
      :text="post.text"
    />
    <img
      v-if="post.type === 'event' && post.event?.hasPhoto"
      class="orangehrm-buzz-post-body-event-photo"
      :src="eventPhotoUrl"
      alt=""
    />
    <event-frame
      v-if="post.type === 'event' && post.event"
      :event="post.event"
    />
    <br v-if="post.text && (post.type === 'video' || post.type === 'photo')" />
    <video-frame v-if="post.type === 'video'" :video-src="post.video.link">
    </video-frame>
    <photo-frame v-if="post.type === 'photo'" :media="post.photoIds">
      <template #content="{index}">
        <div
          v-if="index === 3 && post.photoIds.length === 5"
          class="orangehrm-buzz-post-body-picture --more"
          @click="onClickPicture(index)"
        >
          <oxd-text tag="p" class="orangehrm-buzz-post-body-more">+1</oxd-text>
          <oxd-icon class="orangehrm-buzz-post-body-more" name="images" />
        </div>
        <div
          v-else
          class="orangehrm-buzz-post-body-picture"
          @click="onClickPicture(index)"
        ></div>
      </template>
    </photo-frame>
    <template v-if="originalPost">
      <br v-if="post.text || post.type === 'video' || post.type === 'photo'" />
      <buzz-employee-name
        :employee="originalPost.employee"
        tag="p"
        name-class="orangehrm-buzz-post-body-employee"
      />
      <oxd-text tag="p" class="orangehrm-buzz-post-body-date">
        {{ originalPost.dateTime }}
      </oxd-text>
      <buzz-post-text
        v-if="originalPost.text"
        tag="p"
        :text="originalPost.text"
        text-class="orangehrm-buzz-post-body-original-text"
      />
      <buzz-link-preview v-if="originalPost.text" :text="originalPost.text" />
    </template>
  </div>
</template>

<script>
import {computed, reactive, toRefs} from 'vue';
import useLocale from '@/core/util/composable/useLocale';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';
import PhotoFrame from '@/orangehrmBuzzPlugin/components/PhotoFrame';
import VideoFrame from '@/orangehrmBuzzPlugin/components/VideoFrame';
import EventFrame from '@/orangehrmBuzzPlugin/components/EventFrame';
import BuzzPostText from '@/orangehrmBuzzPlugin/components/BuzzPostText';
import BuzzLinkPreview from '@/orangehrmBuzzPlugin/components/BuzzLinkPreview';
import BuzzEmployeeName from '@/orangehrmBuzzPlugin/components/BuzzEmployeeName';
import {OxdIcon} from '@ohrm/oxd';

export default {
  name: 'PostBody',

  components: {
    'oxd-icon': OxdIcon,
    'photo-frame': PhotoFrame,
    'video-frame': VideoFrame,
    'event-frame': EventFrame,
    'buzz-post-text': BuzzPostText,
    'buzz-link-preview': BuzzLinkPreview,
    'buzz-employee-name': BuzzEmployeeName,
  },

  props: {
    post: {
      type: Object,
      required: true,
    },
  },

  emits: ['close', 'selectPhoto'],

  setup(props, context) {
    const {locale} = useLocale();
    const {jsDateFormat, jsTimeFormat} = useDateFormat();
    const state = reactive({
      readMore: new String(props.post?.text).length < 500,
    });

    const postClasses = computed(() => ({
      'orangehrm-buzz-post-body-text': true,
      '--truncate': state.readMore === false,
    }));

    const onClickReadMore = () => {
      state.readMore = !state.readMore;
    };

    const onClickPicture = (index) => {
      context.emit('selectPhoto', index);
    };

    const eventPhotoUrl = computed(() => {
      if (props.post.type !== 'event' || !props.post.event?.id) {
        return null;
      }
      return `${window.appGlobal.baseUrl}/buzz/event/${props.post.event.id}/photo`;
    });

    const originalPost = computed(() => {
      if (props.post.originalPost === null) return null;
      const {createdDate, createdTime} = props.post.originalPost;

      const utcDate = parseDate(
        `${createdDate} ${createdTime} +00:00`,
        'yyyy-MM-dd HH:mm xxx',
      );

      return {
        text: props.post.originalPost.text,
        employee: props.post.originalPost.employee,
        dateTime: formatDate(utcDate, `${jsDateFormat} ${jsTimeFormat}`, {
          locale,
        }),
      };
    });

    return {
      postClasses,
      eventPhotoUrl,
      originalPost,
      onClickPicture,
      onClickReadMore,
      ...toRefs(state),
    };
  },
};
</script>

<style src="./post-body.scss" lang="scss" scoped></style>
